import { Box, Button, Flex, Icon, Link, Text } from '@chakra-ui/react'
import {
  ChevronDown,
  ChevronUp,
  Info,
  LucideIcon,
  OctagonAlert,
  TriangleAlert,
  X,
} from 'lucide-react'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown, { Components } from 'react-markdown'
import { Link as RouterLink, useNavigate } from 'react-router'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { AnnouncementEvent, AnnouncementView } from '~/hooks/useAnnouncements'
import { BannerIconButton } from '~/pages/Home/Banners/BannerControls'
import {
  AnnouncementSeverity,
  isAllowedAnnouncementLink,
  isInAppPath,
} from '~/utils/announcements'

const SEVERITY_STYLES: Record<
  AnnouncementSeverity,
  { bg: string; accent: string; icon: LucideIcon }
> = {
  info: { bg: 'bg.info', accent: 'info', icon: Info },
  warning: { bg: 'bg.warning', accent: 'warning', icon: TriangleAlert },
  critical: { bg: 'bg.error', accent: 'error', icon: OctagonAlert },
}

const ALLOWED_ELEMENTS = ['p', 'strong', 'em', 'a']

const AnnouncementLink: Components['a'] = ({ href, children }) => {
  if (!href || !isAllowedAnnouncementLink(href)) {
    return <>{children}</>
  }
  if (isInAppPath(href)) {
    return (
      <Link asChild color="inherit" textDecoration="underline">
        <RouterLink to={href}>{children}</RouterLink>
      </Link>
    )
  }
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      color="inherit"
      textDecoration="underline"
    >
      {children}
    </Link>
  )
}

const MARKDOWN_COMPONENTS: Components = {
  p: ({ children }) => (
    <Text as="p" margin={0}>
      {children}
    </Text>
  ),
  a: AnnouncementLink,
}

const MARKDOWN_OPTIONS = {
  allowedElements: ALLOWED_ELEMENTS,
  unwrapDisallowed: true,
  skipHtml: true,
}

export const AnnouncementMarkdown = ({ children }: { children: string }) => (
  <ReactMarkdown {...MARKDOWN_OPTIONS} components={MARKDOWN_COMPONENTS}>
    {children}
  </ReactMarkdown>
)

/**
 * Critical banners are announced through the assertive announcer instead of a
 * native `role=alert`, which some screen readers skip while the page loads and
 * others read in addition to the announcer. Once per announcement version per
 * page load.
 */
const announcedCriticalKeys = new Set<string>()

const textOf = (node: React.ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).join('')
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return textOf(node.props.children)
  }
  return ''
}

/**
 * The text a body renders as, so markdown syntax isn't read aloud. Calling
 * `ReactMarkdown` directly is safe because it's synchronous and uses no hooks;
 * recheck that on upgrades (`MarkdownHooks` is the variant that isn't).
 */
const toSpokenText = (markdown: string) =>
  textOf(ReactMarkdown({ ...MARKDOWN_OPTIONS, children: markdown }))
    .replace(/\s+/g, ' ')
    .trim()

interface AnnouncementBannerProps {
  announcement: AnnouncementView
  /** True when this banner is the one paged into view in the banner slot. */
  isVisible: boolean
  onTrack: (announcement: AnnouncementView, event: AnnouncementEvent) => void
  onDismiss: (announcement: AnnouncementView) => void
  onCollapsedChange: (
    announcement: AnnouncementView,
    collapsed: boolean,
  ) => void
}

export const AnnouncementBanner = ({
  announcement,
  isVisible,
  onTrack,
  onDismiss,
  onCollapsedChange,
}: AnnouncementBannerProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const announce = useAnnounce()
  const { severity, title, body, cta, dismissMode, isCollapsed } = announcement
  const { bg, accent, icon: SeverityIcon } = SEVERITY_STYLES[severity]
  const canCollapse = dismissMode === 'collapse' && !!body
  const isCritical = severity === 'critical'

  const regionLabel = (() => {
    switch (severity) {
      case 'critical':
        return t(
          'announcementBanner.criticalLabel',
          'Critical announcement: {{title}}',
          { title },
        )
      case 'warning':
        return t(
          'announcementBanner.warningLabel',
          'Warning announcement: {{title}}',
          { title },
        )
      default:
        return t(
          'announcementBanner.infoLabel',
          'Info announcement: {{title}}',
          { title },
        )
    }
  })()

  useEffect(() => {
    if (!isVisible) return
    onTrack(announcement, 'shown')
    if (isCritical && !announcedCriticalKeys.has(announcement.key)) {
      announcedCriticalKeys.add(announcement.key)
      const message = body ? `${title}. ${toSpokenText(body)}` : title
      announce(message, 'assertive')
    }
  }, [isVisible, isCritical, announcement, title, body, onTrack, announce])

  const handleCtaClick = useCallback(() => {
    if (!cta) return
    onTrack(announcement, 'cta_clicked')
    if (isInAppPath(cta.url)) {
      void navigate(cta.url)
    } else {
      window.open(cta.url, '_blank', 'noopener,noreferrer')
    }
  }, [announcement, cta, navigate, onTrack])

  return (
    <Flex
      as="section"
      aria-label={regionLabel}
      data-severity={severity}
      bg={bg}
      borderBottomWidth="1px"
      borderColor={`border.${accent}`}
      color="fg.default"
      minHeight="56px"
      width="100%"
      paddingX="16px"
      paddingY="8px"
      gap="12px"
      alignItems="center"
    >
      <Icon color={`fg.${accent}`} boxSize="20px" flexShrink={0} aria-hidden>
        <SeverityIcon size="100%" />
      </Icon>
      <Flex direction="column" flex={1} minWidth={0} fontSize="14px">
        <Text fontWeight="600" lineHeight="20px">
          {title}
        </Text>
        {body && !isCollapsed && (
          <Box lineHeight="20px" color="fg.muted">
            <AnnouncementMarkdown>{body}</AnnouncementMarkdown>
          </Box>
        )}
      </Flex>
      {cta && (
        <Button
          size="sm"
          variant="outline"
          flexShrink={0}
          onClick={handleCtaClick}
        >
          {cta.label}
        </Button>
      )}
      {canCollapse && (
        <BannerIconButton
          aria-label={
            isCollapsed
              ? t('announcementBanner.expand', 'Expand announcement')
              : t('announcementBanner.collapse', 'Collapse announcement')
          }
          aria-expanded={!isCollapsed}
          onClick={() => onCollapsedChange(announcement, !isCollapsed)}
        >
          <Icon boxSize="20px" color="fg.muted" aria-hidden>
            {isCollapsed ? (
              <ChevronDown size="100%" />
            ) : (
              <ChevronUp size="100%" />
            )}
          </Icon>
        </BannerIconButton>
      )}
      {dismissMode === 'dismiss' && (
        <BannerIconButton
          aria-label={t('announcementBanner.dismiss', 'Dismiss announcement')}
          onClick={() => onDismiss(announcement)}
        >
          <Icon boxSize="20px" color="fg.muted" aria-hidden>
            <X size="100%" />
          </Icon>
        </BannerIconButton>
      )}
    </Flex>
  )
}
